import { promises as fs } from "node:fs";
import path from "node:path";

import { runGitCommand } from "./run-git-command.js";

const APPOSTLE_TMP_IGNORE_RULE = ".appostle/tmp/";
const APPOSTLE_TMP_IGNORE_VARIANTS = new Set([
  ".appostle/tmp/",
  ".appostle/tmp",
  "/.appostle/tmp/",
  "/.appostle/tmp",
]);

/**
 * Ensure `<rootPath>/.gitignore` excludes the machine-local `.appostle/tmp/`
 * directory while leaving the rest of `.appostle/` trackable. Appends an
 * idempotent block; never rewrites or reorders existing ignore rules.
 */
export async function ensureAppostleTmpIgnored(rootPath: string): Promise<void> {
  const gitignorePath = path.join(rootPath, ".gitignore");

  let existing = "";
  try {
    existing = await fs.readFile(gitignorePath, "utf8");
  } catch (error) {
    if ((error as NodeJS.ErrnoException).code !== "ENOENT") {
      throw error;
    }
  }

  const alreadyIgnored = existing
    .split(/\r?\n/)
    .map((line) => line.trim())
    .some((line) => APPOSTLE_TMP_IGNORE_VARIANTS.has(line));
  if (alreadyIgnored) {
    return;
  }

  const needsLeadingNewline = existing.length > 0 && !existing.endsWith("\n");
  const block = `${needsLeadingNewline ? "\n" : ""}# Appostle machine-local temp — never shared\n${APPOSTLE_TMP_IGNORE_RULE}\n`;
  await fs.appendFile(gitignorePath, block, "utf8");
}

/**
 * Initialize a fresh git repository at `cwd` with `main` as the default branch
 * and an empty initial commit so the branch is born immediately (a workspace
 * with an unborn HEAD renders with no branch name). The empty commit is
 * best-effort: if git has no committer identity configured the repo is still a
 * valid git checkout, so we log nothing and let the caller proceed.
 */
export async function initRepoWithEmptyCommit(cwd: string): Promise<void> {
  await runGitCommand(["init", "-b", "main"], { cwd });
  await ensureAppostleTmpIgnored(cwd);
  try {
    await runGitCommand(["commit", "--allow-empty", "-m", "Initial commit"], { cwd });
  } catch {
    // No committer identity (user.name/user.email) — the repo is still a valid
    // git checkout, just without a born branch. Leave it for the user to make
    // their first real commit.
  }
}

/**
 * Clone `remoteUrl` into `destPath`. Tolerates a pre-existing `.appostle/`
 * directory in the destination (seeded on a prior open) by stashing it aside,
 * cloning into the now-empty directory, then merging the seed back in for any
 * files the clone did not provide. Relies on ambient git credentials (ssh key
 * or `GIT_*`/credential helper) — the same model `gitlab-service` uses.
 */
export async function cloneRepo(remoteUrl: string, destPath: string): Promise<void> {
  const parent = path.dirname(destPath);
  await fs.mkdir(destPath, { recursive: true });

  const entries = await fs.readdir(destPath);
  const nonAppostleEntries = entries.filter((name) => name !== ".appostle");
  const hasAppostleSeed = entries.includes(".appostle");

  if (nonAppostleEntries.length > 0) {
    // Non-empty in a way git clone can't merge — surface a clear error rather
    // than the cryptic "destination path already exists" from git.
    throw new Error(
      `Cannot clone into ${destPath}: directory is not empty (${nonAppostleEntries.length} existing item(s)). Clone into a fresh folder.`,
    );
  }

  let stashPath: string | null = null;
  if (hasAppostleSeed) {
    stashPath = path.join(parent, `.appostle-clone-stash-${Date.now()}-${process.pid}`);
    await fs.rename(path.join(destPath, ".appostle"), stashPath);
  }

  try {
    // The destination must be empty (or absent) for `git clone <url> <dest>`.
    // After stashing `.appostle` it is, so remove and let git recreate it.
    await fs.rmdir(destPath).catch(() => {});
    await runGitCommand(["clone", remoteUrl, destPath], {
      cwd: parent,
      timeout: 300_000,
    });
  } catch (error) {
    // Restore the seed so a failed clone doesn't lose the user's `.appostle`.
    if (stashPath) {
      await fs.mkdir(destPath, { recursive: true });
      await fs.rename(stashPath, path.join(destPath, ".appostle")).catch(() => {});
    }
    throw error;
  }

  if (stashPath) {
    await mergeStashedAppostleSeed(stashPath, path.join(destPath, ".appostle"));
  }

  await ensureAppostleTmpIgnored(destPath);
}

/**
 * Copy any files from the stashed `.appostle` seed that the clone did not
 * provide, then remove the stash. The clone's own `.appostle` (if the remote
 * committed one) wins on conflict — it's the shared source of truth.
 */
async function mergeStashedAppostleSeed(stashPath: string, targetPath: string): Promise<void> {
  try {
    await fs.cp(stashPath, targetPath, { recursive: true, force: false, errorOnExist: false });
  } finally {
    await fs.rm(stashPath, { recursive: true, force: true }).catch(() => {});
  }
}
